import React from "react";
import styled from "styled-components";
import {Link as LinkIcon} from "@material-ui/icons";
import {Link} from "react-router";
import {IconButton} from "@material-ui/core";

import Fieldset from "./styled/fieldset";
import Legend from "./styled/legend";
import calculateDuration from "../../utils/calculate_duration";
import renderDuration from "../../utils/render_duration";
import {formatDate} from "common/utils/format_date";

import getStatus from "routes/llm_run/utils/get_status";

import {EnhancedLlmRun} from "common/flowmaster/types/llm_run";
import {ActionOutput} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";

interface Props {
  llmRun: EnhancedLlmRun;
  organisationId: OrganisationId;
}

const Container = styled(Fieldset)`
  padding-left: 1em;
`;

const KeyValues = styled.table`
  margin-right: 1em;
`;

const RowHeading = styled.th`
  text-align: right;
`;

const Matrix = styled.table`
  background-color: #eee;
  border-spacing: 0.25em;
  padding: 0.5em;
`;

function getMeta(item: string | ActionOutput, key: string): number {
  return ((item as ActionOutput)?.meta as Record<string, unknown>)?.[
    key
  ] as number;
}

export default function Inputs({llmRun, organisationId}: Props) {
  const tasks: Array<
    [number, ActionOutput]
  > = llmRun.all_task_data.map(item => [
    item.workflow_task_id,
    item.data as ActionOutput,
  ]);
  const totalCost = tasks.reduce(
    (total, [, item]) => total + (getMeta(item, "cost") ?? 0),
    0,
  );
  const totalTokens = tasks.reduce(
    (total, [, item]) => total + (getMeta(item, "total_tokens") ?? 0),
    0,
  );
  const maxCost = tasks.reduce(
    (best, [id, item]) => {
      const cost = getMeta(item, "cost");
      if (cost > best.max) {
        return {max: cost, id, item};
      }
      return best;
    },
    {max: 0, id: null, item: null},
  );
  const maxTokens = tasks.reduce(
    (best, [id, item]) => {
      const totalTokens = getMeta(item, "total_tokens");
      if (totalTokens > best.max) {
        return {max: totalTokens, id, item};
      }
      return best;
    },
    {max: 0, id: null, item: null},
  );
  const status = getStatus(llmRun);
  const {next_task: nextTask} = llmRun.data;
  const nextTaskText =
    status === "In progress" && nextTask !== null ? (
      <span style={{fontSize: "0.75em"}}>
        {" (Next task: "}
        <span style={{fontFamily: "monospace"}}>
          {(llmRun.tasks ?? []).find(t => t.id === nextTask.id)?.name}
        </span>
        )
      </span>
    ) : (
      ""
    );

  let dataAssetLink: string | null = null;
  if (llmRun.creation_reason === "data_asset") {
    const {workflow_id, workflow_instance_id} = llmRun.data;
    const {project_id, document_id} = llmRun.data.data_store.context;

    dataAssetLink = `/organisation/${organisationId}/data-assets/definition/${workflow_id}/config/${workflow_instance_id}/asset/${llmRun.id}?project=${project_id}&document=${document_id}`;
  }

  const startTime = llmRun.data.states_performed?.[0]?.start_time;

  return (
    <Container>
      <Legend>Summary</Legend>
      <div style={{display: "flex"}}>
        <KeyValues>
          <tbody>
            <tr>
              <RowHeading>Run ID:</RowHeading>
              <td style={{fontFamily: "monospace"}}> {llmRun.run_id}</td>
            </tr>
            {llmRun.username && (
              <tr>
                <RowHeading>User:</RowHeading>
                <td> {llmRun.username}</td>
              </tr>
            )}
            <tr>
              <RowHeading>Status:</RowHeading>
              <td>
                {getStatus(llmRun)}
                {nextTaskText}
              </td>
            </tr>
            <tr>
              <RowHeading>Reason:</RowHeading>
              <td>
                <div style={{display: "flex", alignItems: "center"}}>
                  {llmRun.creation_reason}
                  {dataAssetLink && (
                    <Link to={dataAssetLink}>
                      <IconButton
                        size="small"
                        style={{padding: 0, marginLeft: "4px"}}
                      >
                        <LinkIcon />
                      </IconButton>
                    </Link>
                  )}
                </div>
              </td>
            </tr>
            <tr>
              <RowHeading>Created:</RowHeading>
              <td> {formatDate(llmRun.creation_date)}</td>
            </tr>
            <tr>
              <RowHeading>Started:</RowHeading>
              <td>
                {startTime ? formatDate(new Date(startTime)) : "Not started"}
              </td>
            </tr>
            <tr>
              <RowHeading>Duration: </RowHeading>
              <td>
                {renderDuration(
                  calculateDuration(
                    llmRun.creation_date,
                    llmRun.data.states_performed?.at(-1)?.end_time ??
                      llmRun.creation_date,
                  ),
                )}
              </td>
            </tr>
            {llmRun.child_run_count > 0 && (
              <tr>
                <RowHeading>Child Runs:</RowHeading>
                <td>
                  <div style={{display: "flex", alignItems: "center"}}>
                    {llmRun.child_run_count}
                    <Link
                      to={`/organisation/${organisationId}/llm_run/list?parent=${llmRun.run_id}&from=${llmRun.creation_date}`}
                    >
                      <IconButton size="small" style={{padding: 0}}>
                        <LinkIcon />
                      </IconButton>
                    </Link>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </KeyValues>
        <Matrix>
          <thead>
            <tr>
              <th></th>
              <th>Cost</th>
              <th>Tokens</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Total: </th>
              <td>${totalCost}</td>
              <td>{totalTokens}</td>
            </tr>
            <tr>
              <th>Max: </th>
              <td title={`id = ${maxCost.id}`}>${maxCost.max}</td>
              <td title={`id = ${maxTokens.id}`}>{maxTokens.max}</td>
            </tr>
          </tbody>
        </Matrix>
        {llmRun.child_cost ? (
          <Matrix style={{marginLeft: "1em"}}>
            <thead>
              <tr>
                <th></th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Parent</th>
                <td>${(llmRun.parent_cost ?? 0).toFixed(4)}</td>
              </tr>
              <tr>
                <th>Child</th>
                <td>${(llmRun.child_cost ?? 0).toFixed(4)}</td>
              </tr>
            </tbody>
          </Matrix>
        ) : null}
      </div>
    </Container>
  );
}
